import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  UserPlus,
  Pencil,
  Trash2,
  Users,
  Shield,
  User,
} from 'lucide-react';

interface Usuario {
  id: string;
  nombre: string;
  email: string;
  rol: string;
  activo: boolean;
  created_at: string;
}

export default function AdminUsuarios() {
  const navigate = useNavigate();
  const { usuario: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; usuario: Usuario | null }>({ open: false, usuario: null });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; usuario: Usuario | null }>({ open: false, usuario: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Form state for create
  const [createForm, setCreateForm] = useState({ nombre: '', email: '', password: '', rol: 'usuario' });
  // Form state for edit
  const [editForm, setEditForm] = useState({ nombre: '', email: '', rol: 'usuario', activo: true, password: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-usuarios'],
    queryFn: () => api.getUsuarios(),
  });

  const usuarios = data?.usuarios || [];

  const handleCreate = async () => {
    setError('');
    if (!createForm.nombre || !createForm.email || !createForm.password) {
      setError('Todos los campos son requeridos');
      return;
    }
    if (createForm.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setIsProcessing(true);
    try {
      await api.createUsuario(createForm);
      queryClient.invalidateQueries({ queryKey: ['admin-usuarios'] });
      setCreateModal(false);
      setCreateForm({ nombre: '', email: '', password: '', rol: 'usuario' });
    } catch (err: any) {
      setError(err.message || 'Error al crear usuario');
    } finally {
      setIsProcessing(false);
    }
  };

  const openEditModal = (usuario: Usuario) => {
    setEditForm({ nombre: usuario.nombre, email: usuario.email, rol: usuario.rol, activo: usuario.activo, password: '' });
    setEditModal({ open: true, usuario });
    setError('');
  };

  const handleEdit = async () => {
    if (!editModal.usuario) return;
    setError('');

    const data: any = {};
    if (editForm.nombre !== editModal.usuario.nombre) data.nombre = editForm.nombre;
    if (editForm.email !== editModal.usuario.email) data.email = editForm.email;
    if (editForm.rol !== editModal.usuario.rol) data.rol = editForm.rol;
    if (editForm.activo !== editModal.usuario.activo) data.activo = editForm.activo;
    if (editForm.password) {
      if (editForm.password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return;
      }
      data.password = editForm.password;
    }

    if (Object.keys(data).length === 0) {
      setEditModal({ open: false, usuario: null });
      return;
    }

    setIsProcessing(true);
    try {
      await api.updateUsuario(editModal.usuario.id, data);
      queryClient.invalidateQueries({ queryKey: ['admin-usuarios'] });
      setEditModal({ open: false, usuario: null });
    } catch (err: any) {
      setError(err.message || 'Error al actualizar usuario');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.usuario) return;
    setIsProcessing(true);
    try {
      await api.deleteUsuario(deleteModal.usuario.id);
      queryClient.invalidateQueries({ queryKey: ['admin-usuarios'] });
      setDeleteModal({ open: false, usuario: null });
    } catch (err: any) {
      setError(err.message || 'Error al eliminar usuario');
    } finally {
      setIsProcessing(false);
    }
  };

  if (currentUser?.rol !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Acceso denegado</h2>
          <p className="text-muted-foreground mt-2">Solo los administradores pueden acceder a esta pagina.</p>
          <Button onClick={() => navigate('/')} className="mt-4">Volver al Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold">Administrar Usuarios</h1>
                  <p className="text-sm text-muted-foreground">{usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''} registrado{usuarios.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>
            <Button onClick={() => { setCreateModal(true); setError(''); }}>
              <UserPlus className="h-4 w-4 mr-2" />
              Crear Usuario
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Cargando usuarios...</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.map((u: Usuario) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {u.rol === 'admin' ? (
                          <Shield className="h-4 w-4 text-yellow-600" />
                        ) : (
                          <User className="h-4 w-4 text-gray-400" />
                        )}
                        {u.nombre}
                        {u.id === currentUser?.id && (
                          <span className="text-xs text-muted-foreground">(tu)</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Badge variant={u.rol === 'admin' ? 'default' : 'secondary'}>
                        {u.rol === 'admin' ? 'Administrador' : 'Usuario'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.activo ? 'success' : 'destructive'}>
                        {u.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditModal(u)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {u.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => { setDeleteModal({ open: true, usuario: u }); setError(''); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Modal Crear Usuario */}
      <Dialog open={createModal} onOpenChange={(open) => { if (!open) { setCreateModal(false); setError(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Crear Usuario
            </DialogTitle>
            <DialogDescription>Complete los datos para crear un nuevo usuario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Nombre</label>
              <Input
                value={createForm.nombre}
                onChange={(e) => setCreateForm({ ...createForm, nombre: e.target.value })}
                placeholder="Nombre completo"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Contraseña</label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Minimo 6 caracteres"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Rol</label>
              <Select value={createForm.rol} onValueChange={(v) => setCreateForm({ ...createForm, rol: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="usuario">Usuario</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModal(false)} disabled={isProcessing}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={isProcessing}>
              {isProcessing ? 'Creando...' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Usuario */}
      <Dialog open={editModal.open} onOpenChange={(open) => { if (!open) { setEditModal({ open: false, usuario: null }); setError(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Editar Usuario
            </DialogTitle>
            <DialogDescription>Modifique los datos del usuario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Nombre</label>
              <Input
                value={editForm.nombre}
                onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nueva Contraseña (dejar vacio para no cambiar)</label>
              <Input
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Dejar vacio para no cambiar"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Rol</label>
              <Select value={editForm.rol} onValueChange={(v) => setEditForm({ ...editForm, rol: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="usuario">Usuario</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Estado</label>
              <Select value={editForm.activo ? 'true' : 'false'} onValueChange={(v) => setEditForm({ ...editForm, activo: v === 'true' })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Activo</SelectItem>
                  <SelectItem value="false">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, usuario: null })} disabled={isProcessing}>Cancelar</Button>
            <Button onClick={handleEdit} disabled={isProcessing}>
              {isProcessing ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Eliminar Usuario */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => { if (!open) setDeleteModal({ open: false, usuario: null }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Eliminar Usuario
            </DialogTitle>
            <DialogDescription>
              Esta seguro que desea eliminar este usuario? Esta accion no se puede deshacer.
              {deleteModal.usuario && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <p className="font-medium">{deleteModal.usuario.nombre}</p>
                  <p className="text-sm">{deleteModal.usuario.email}</p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, usuario: null })} disabled={isProcessing}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isProcessing}>
              {isProcessing ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
